import type { TableColumns } from '@ohpen/enso-ui';
import type { TFunction } from 'i18next';
import { CompliancePermissionLevel, ComplianceStatus, FieldType } from '@pmp/api';

import { COMPLIANCE_PERMISSION_LEVELS, SETTABLE_COMPLIANCE_STATUSES } from './utils';

export const LENDER_ENTITY_LIST_NAMES: FieldType[] = [FieldType.COMPLIANCE_STATUS, FieldType.YES_NO];

// CompliancePermissionLevel has no backend-served value list (no matching FieldType), so its
// options are built client-side and injected into the table's valueLists under this local list name.
export const LENDER_COMPLIANCE_PERMISSION_LEVEL_LIST = 'lenderCompliancePermissionLevel';

export const buildLenderPermissionLevelValueList = (t: TFunction): Record<string, string> =>
  COMPLIANCE_PERMISSION_LEVELS.reduce(
    (acc, level) => ({ ...acc, [level]: t(`lender.lender.labels.${level}`, { defaultValue: level }) }),
    {} as Record<string, string>,
  );

export const lenderFirmPermissionColumnKey = (status: ComplianceStatus): string => `firmPermission${status}`;
export const lenderIntermediaryPermissionColumnKey = (status: ComplianceStatus): string => `intermediaryPermission${status}`;

// Extended type: flattens the per-status permission arrays into scalar columns for the grid/tabs.
// Address isn't shown here - it isn't editable anywhere in the Lender UI today.
export type LenderCRDataRow = {
  rowNumber: number;
  id?: string | null;
  defaultFirmStatus?: ComplianceStatus | null;
  defaultIntermediaryStatus?: ComplianceStatus | null;
  firmToReviewPermissionLevel?: CompliancePermissionLevel | null;
  intermediaryToReviewPermissionLevel?: CompliancePermissionLevel | null;
  [key: `firmPermission${string}`]: CompliancePermissionLevel | null | undefined;
  [key: `intermediaryPermission${string}`]: CompliancePermissionLevel | null | undefined;
  resetReviewRequiredFlagAfterMonths?: number | null;
  reviewOnBmpChangesEnabled?: boolean | null;
  periodicCheckEnabled?: boolean | null;
  lockedByActionId?: string | null;
};

export const lenderCRColumns: TableColumns<LenderCRDataRow> = [
  {
    title: 'lender.lender.labels.firmInitialCompliance',
    dataIndex: 'defaultFirmStatus',
    key: 'defaultFirmStatus',
    width: 160,
    options: {
      format: { type: 'valueList' },
      filter: 'select',
      listName: FieldType.COMPLIANCE_STATUS,
    },
  },
  {
    title: 'lender.lender.labels.intermediaryInitialCompliance',
    dataIndex: 'defaultIntermediaryStatus',
    key: 'defaultIntermediaryStatus',
    width: 160,
    options: {
      format: { type: 'valueList' },
      filter: 'select',
      listName: FieldType.COMPLIANCE_STATUS,
    },
  },
  {
    title: 'lender.lender.labels.reviewOnBmpChanges',
    dataIndex: 'reviewOnBmpChangesEnabled',
    key: 'reviewOnBmpChangesEnabled',
    width: 120,
    options: {
      format: { type: 'boolean' },
      filter: 'select',
      listName: FieldType.YES_NO,
    },
  },
  {
    title: 'lender.lender.labels.periodicCheck',
    dataIndex: 'periodicCheckEnabled',
    key: 'periodicCheckEnabled',
    width: 120,
    options: {
      format: { type: 'boolean' },
      filter: 'select',
      listName: FieldType.YES_NO,
    },
  },
  {
    title: 'lender.lender.labels.reviewInterval',
    dataIndex: 'resetReviewRequiredFlagAfterMonths',
    key: 'resetReviewRequiredFlagAfterMonths',
    width: 140,
    options: {
      format: { type: 'number' },
    },
  },
];

const permissionLevelOptions = {
  format: { type: 'valueList' as const },
  filter: 'select' as const,
  listName: LENDER_COMPLIANCE_PERMISSION_LEVEL_LIST,
};

export const firmPermissionCRColumns: TableColumns<LenderCRDataRow> = [
  ...SETTABLE_COMPLIANCE_STATUSES.map(status => ({
    title: `lender.lender.labels.${status}`,
    dataIndex: lenderFirmPermissionColumnKey(status),
    key: lenderFirmPermissionColumnKey(status),
    width: 160,
    options: permissionLevelOptions,
  })),
  {
    title: 'lender.lender.labels.reviewRequiredPermission',
    dataIndex: 'firmToReviewPermissionLevel',
    key: 'firmToReviewPermissionLevel',
    width: 160,
    options: permissionLevelOptions,
  },
];

export const intermediaryPermissionCRColumns: TableColumns<LenderCRDataRow> = [
  ...SETTABLE_COMPLIANCE_STATUSES.map(status => ({
    title: `lender.lender.labels.${status}`,
    dataIndex: lenderIntermediaryPermissionColumnKey(status),
    key: lenderIntermediaryPermissionColumnKey(status),
    width: 160,
    options: permissionLevelOptions,
  })),
  {
    title: 'lender.lender.labels.reviewRequiredPermission',
    dataIndex: 'intermediaryToReviewPermissionLevel',
    key: 'intermediaryToReviewPermissionLevel',
    width: 160,
    options: permissionLevelOptions,
  },
];

// Derived once from the static column configs above - these never change, so callers can use them
// directly instead of re-deriving via a `useMemo(..., [])` on every render.
export const firmPermissionColumnKeys: string[] = firmPermissionCRColumns
  .map(c => c.dataIndex)
  .filter((k): k is string => typeof k === 'string');

export const intermediaryPermissionColumnKeys: string[] = intermediaryPermissionCRColumns
  .map(c => c.dataIndex)
  .filter((k): k is string => typeof k === 'string');
